import { useState, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Cookies from 'js-cookie';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

/**
 * Hook to manage critical announcement modal state based on LaunchDarkly config, dispensary state restrictions, and cookie dismissal
 * @returns {Object} Object containing showModal boolean and modal config
 */
export default function useCriticalAnnouncementModal() {
  const flags = useFlags();
  const { dispensary } = useDispensary();
  const [modalConfig, setModalConfig] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const flagConfig = flags['growth.ecomm.critical-checkout-announcement.config'];

    // Check if flag is enabled and has valid config
    // eslint-disable-next-line
    if (!flagConfig || typeof flagConfig !== 'object') {
      setShowModal(false);
      setModalConfig(null);
      return;
    }

    const { name, content, restrictions } = flagConfig;

    // Validate required fields
    if (!name || !content) {
      setShowModal(false);
      setModalConfig(null);
      return;
    }

    // Check state restrictions if they exist
    if (restrictions?.state && Array.isArray(restrictions.state)) {
      const dispensaryState = dispensary?.location?.state;
      if (!dispensaryState || !restrictions.state.includes(dispensaryState)) {
        setShowModal(false);
        setModalConfig(null);
        return;
      }
    }

    // Check if this announcement has been dismissed
    const isDismissed = Cookies.get(`dtch-announcement-dismissed-${name}`) === 'true';

    if (isDismissed) {
      setShowModal(false);
      setModalConfig(null);
      return;
    }

    // Show modal if flag is enabled, state matches (if restricted), and not dismissed
    setModalConfig(flagConfig);
    setShowModal(true);
  }, [flags, dispensary]);

  const closeModal = () => {
    setShowModal(false);
  };

  return {
    showModal,
    modalConfig,
    closeModal,
  };
}
